import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hammer, ShieldCheck, Home, Wrench, Briefcase, ArrowRight, Star, Check, Quote, ChevronLeft, ChevronRight, Phone, X } from 'lucide-react';
import { getServices, getGalleryItems, getTestimonials, getPriceExamples } from '../lib/cms';

// Dynamisk ikon-komponent baseret på ikonavn fra CMS
const DynamicIcon = ({ name, className }) => {
  const icons = {
    Hammer: Hammer,
    ShieldCheck: ShieldCheck,
    Home: Home,
    Wrench: Wrench,
    Briefcase: Briefcase,
  };
  const IconComponent = icons[name] || Hammer;
  return <IconComponent className={className} />;
};

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [priceExamples, setPriceExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [count, setCount] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: 'Tagarbejde',
    message: '',
    website_url: '', // Honeypot antispam felt
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    // Hvis honeypot-feltet er udfyldt, afviser vi lydløst
    if (formData.website_url) {
      setTimeout(() => {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: 'Tagarbejde',
          message: '',
          website_url: '',
        });
      }, 1000);
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          projectType: formData.projectType,
          message: formData.message,
          website_url: formData.website_url,
        }),
      });

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: 'Tagarbejde',
          message: '',
          website_url: '',
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der opstod en fejl ved afsendelse af formularen.');
      }
    } catch (err) {
      console.error('Fejl ved indsendelse af kontaktformular:', err);
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Kunne ikke oprette forbindelse til serveren. Prøv igen senere.',
      });
    }
  };

  const handlePrevTestimonial = () => {
    setTestimonialIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNextTestimonial = () => {
    setTestimonialIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  // Hent de udtalelser der skal vises (vi viser 3 af gangen på desktop, 2 på tablet, og 1 på mobil)
  const getVisibleTestimonials = () => {
    if (testimonials.length === 0) return [];
    const first = testimonials[testimonialIndex];
    const second = testimonials[(testimonialIndex + 1) % testimonials.length];
    const third = testimonials[(testimonialIndex + 2) % testimonials.length];
    return [
      { ...first, visibility: 'always' },
      { ...second, visibility: 'tablet-desktop' },
      { ...third, visibility: 'desktop-only' }
    ];
  };

  useEffect(() => {
    let start = 0;
    const end = 100;
    const duration = 1500; // 1.5 sekunder
    const stepTime = Math.floor(duration / end);
    
    const timer = setInterval(() => {
      start += 1;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    async function loadData() {
      try {
        const [servicesData, galleryData, testimonialsData, priceExamplesData] = await Promise.all([
          getServices(),
          getGalleryItems(),
          getTestimonials(),
          getPriceExamples(),
        ]);
        setServices(servicesData.slice(0, 4)); // Vis de første 4 ydelser på forsiden i et 2-kolonne layout
        setPriceExamples(priceExamplesData);
        
        // Saml alle billeder på tværs af alle gallerier
        const allImages = [];
        galleryData.forEach((item) => {
          if (item.image && Array.isArray(item.image)) {
            item.image.forEach((imgUrl, idx) => {
              allImages.push({
                id: `${item.id}-${idx}`,
                slug: item.slug?._ || item.slug,
                title: item.title,
                imageUrl: imgUrl,
              });
            });
          }
        });

        // Bland billederne tilfældigt og vælg 3
        const shuffled = [...allImages].sort(() => 0.5 - Math.random());
        setGalleryItems(shuffled.slice(0, 3));
        
        setTestimonials(testimonialsData);
      } catch (err) {
        console.error('Fejl ved indlæsning af forside-data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // SEO Titel og Meta Description for forsiden
  useEffect(() => {
    document.title = "Entreprenør på Sjælland | Professionel Tømrer & Tagarbejde";
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Entreprenør på Sjælland med fokus på kvalitet, tagarbejde, tømrerarbejde og totalentreprise. Få ærlig rådgivning og et uforpligtende tilbud.');
  }, []);

  return (
    <div className="bg-slate-50">
      {/* Asbest Marquee USP Bar */}
      <section className="relative w-full overflow-hidden bg-blue-900 py-5 border-y border-blue-800/50 shadow-inner z-20">
        <div className="flex w-max animate-marquee">
          {/* Første sæt af elementer */}
          <div className="flex items-center gap-12 px-6">
            {/* Gruppe 1 */}
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-shield-halved text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Certificeret asbestarbejde
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-mask-ventilator text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Sikker håndtering & sanering
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-circle-check text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Overholder alle lovkrav
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-triangle-exclamation text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Professionelt udstyr & sikkerhed
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-recycle text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Miljørigtig bortskaffelse
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-house-shield text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Din trygge entreprenør
              </span>
            </div>

            {/* Gruppe 2 */}
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-shield-halved text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Certificeret asbestarbejde
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-mask-ventilator text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Sikker håndtering & sanering
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-circle-check text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Overholder alle lovkrav
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-triangle-exclamation text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Professionelt udstyr & sikkerhed
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-recycle text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Miljørigtig bortskaffelse
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-house-shield text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Din trygge entreprenør
              </span>
            </div>

            {/* Gruppe 3 */}
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-shield-halved text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Certificeret asbestarbejde
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-mask-ventilator text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Sikker håndtering & sanering
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-circle-check text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Overholder alle lovkrav
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-triangle-exclamation text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Professionelt udstyr & sikkerhed
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-recycle text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Miljørigtig bortskaffelse
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-house-shield text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Din trygge entreprenør
              </span>
            </div>
          </div>

          {/* Andet sæt af elementer (identisk kopi for sømløs løkke) */}
          <div className="flex items-center gap-12 px-6" aria-hidden="true">
            {/* Gruppe 1 */}
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-shield-halved text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Certificeret asbestarbejde
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-mask-ventilator text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Sikker håndtering & sanering
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-circle-check text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Overholder alle lovkrav
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-triangle-exclamation text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Professionelt udstyr & sikkerhed
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-recycle text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Miljørigtig bortskaffelse
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-house-shield text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Din trygge entreprenør
              </span>
            </div>

            {/* Gruppe 2 */}
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-shield-halved text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Certificeret asbestarbejde
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-mask-ventilator text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Sikker håndtering & sanering
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-circle-check text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Overholder alle lovkrav
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-triangle-exclamation text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Professionelt udstyr & sikkerhed
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-recycle text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Miljørigtig bortskaffelse
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-house-shield text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Din trygge entreprenør
              </span>
            </div>

            {/* Gruppe 3 */}
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-shield-halved text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Certificeret asbestarbejde
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-mask-ventilator text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Sikker håndtering & sanering
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-circle-check text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Overholder alle lovkrav
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-triangle-exclamation text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Professionelt udstyr & sikkerhed
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-recycle text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Miljørigtig bortskaffelse
              </span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fa-solid fa-house-shield text-amber-400 text-lg" aria-hidden="true" />
              <span className="text-sm md:text-base font-extrabold uppercase tracking-widest text-white whitespace-nowrap">
                Din trygge entreprenør
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Sektion */}
      <section className="relative bg-blue-950 text-white overflow-hidden py-16 lg:py-24">
        {/* Baggrundsbillede med overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/b4f7e7e4-b57e-4a68-b3f8-2a4e7f0b8617/tagrenovering-entreprenoer-sjaelland-toa-byg.jpg"
            alt="Professionel tagrenovering og entreprenørarbejde på Sjælland udført af Toa Byg ApS"
            className="w-full h-full object-cover object-center opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-950 via-blue-950/95 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Venstre side: Hero tekst */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-2 text-xs text-white font-semibold mb-6 uppercase tracking-wider">
                <ShieldCheck className="h-4 w-4 text-white" />
                <span>Professionelt håndværk · Sikret kvalitet</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight uppercase">
                Entreprenør på Sjælland - <br />
                <span className="text-blue-400">når byggeriet skal føles trygt</span>
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-slate-300 leading-relaxed max-w-xl">
                Toa Byg ApS er din entreprenør på Sjælland, når du ønsker solidt håndværk, ærlig rådgivning og en løsning, der passer til både huset og hverdagen. Fra vores base i Ishøj hjælper vi private og erhverv med tømrerarbejde, renovering, ombygning og større byggeprojekter.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-6 py-3.5 border border-white text-base font-semibold rounded-none text-blue-950 bg-white hover:bg-slate-100 transition-all uppercase tracking-wider shadow-lg"
                >
                  Kontakt os
                </Link>
              </div>
            </div>

            {/* Højre side: Webform */}
            <div className="lg:col-span-5 lg:translate-y-6">
              <div className="bg-white text-slate-900 p-6 sm:p-8 rounded-none shadow-2xl border border-slate-100">
                <h3 className="text-xl sm:text-2xl font-extrabold mb-1 uppercase tracking-wider">Få et uforpligtende tilbud</h3>
                <p className="text-slate-500 text-xs sm:text-sm mb-6 uppercase tracking-wider">Vi kontakter dig inden for 24 timer</p>

                {status.success ? (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-none flex items-start gap-3 animate-in fade-in duration-300">
                    <div>
                      <h4 className="font-bold text-emerald-900 uppercase text-sm">Tak for din henvendelse!</h4>
                      <p className="text-xs text-emerald-700 mt-1">
                        Vi har modtaget din besked og vil kontakte dig hurtigst muligt.
                      </p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* ANTISPAM HONEYPOT FIELD - ABSOLUTELY HIDDEN FROM HUMANS */}
                    <div className="sr-only absolute" style={{ width: 0, height: 0, overflow: 'hidden' }}>
                      <label htmlFor="hero_website_url">Efterlad dette felt tomt</label>
                      <input
                        type="text"
                        id="hero_website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    {status.error && (
                      <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-none text-xs">
                        {status.error}
                      </div>
                    )}

                    <div>
                      <label htmlFor="hero_name" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                        Navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="hero_name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 text-sm rounded-none border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 outline-none transition-all text-slate-900"
                        placeholder="Dit fulde navn"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="hero_phone" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                          Telefon <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="hero_phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-3 py-2 text-sm rounded-none border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 outline-none transition-all text-slate-900"
                          placeholder="Telefonnummer"
                        />
                      </div>
                      <div>
                        <label htmlFor="hero_email" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                          E-mail <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="hero_email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-3 py-2 text-sm rounded-none border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 outline-none transition-all text-slate-900"
                          placeholder="E-mailadresse"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="hero_projectType" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                        Hvad drejer opgaven sig om?
                      </label>
                      <select
                        id="hero_projectType"
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleChange}
                        className="w-full px-3 py-2 text-sm rounded-none border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 outline-none transition-all text-slate-900 bg-white"
                      >
                        <option value="Tagarbejde">Tagarbejde</option>
                        <option value="Tagrenovering">Tagrenovering</option>
                        <option value="Renovering">Renovering</option>
                        <option value="Tømrerarbejde">Tømrerarbejde</option>
                        <option value="Entrepriseopgaver">Entrepriseopgaver</option>
                        <option value="Andet">Andet</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="hero_message" className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                        Besked / Beskrivelse
                      </label>
                      <textarea
                        id="hero_message"
                        name="message"
                        rows="3"
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-3 py-2 text-sm rounded-none border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 outline-none transition-all text-slate-900 resize-none"
                        placeholder="Kort beskrivelse af opgaven..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={status.submitting}
                      className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-semibold rounded-none text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 transition-colors shadow-md cursor-pointer uppercase tracking-wider"
                    >
                      {status.submitting ? 'Sender...' : 'Få uforpligtende tilbud'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduktion & Værdier */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">
            {/* Venstre: Tekst */}
            <div>
              <span className="text-blue-600 font-bold text-sm uppercase tracking-widest block mb-2">Troværdigt byggefirma</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight uppercase">
                Hvorfor vælge et byggefirma på Sjælland med lokalt kendskab
              </h2>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                Som byggefirma på Sjælland ved vi, hvor meget det betyder, at aftaler bliver overholdt, og at arbejdet bliver gjort ordentligt første gang. Vi møder hver opgave med respekt for bygningen, budgettet og den tillid, du viser os, når du lukker os ind i dit projekt.
              </p>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                Hos Toa Byg ApS forener vi lokalt kendskab med solidt håndværk og professionel styring. Vi tager os af alt fra mindre tømreropgaver til totalentreprise, hvor flere fag skal spille sammen. Det giver dig en mere overskuelig proces, fordi vi hjælper med planlægning, koordinering og den praktiske udførelse, så du ikke står alene med beslutningerne.
              </p>
              <div className="pt-6 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-sm font-semibold rounded-none text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-md uppercase tracking-wider"
                >
                  Få et uforpligtende tilbud
                </Link>
                <a
                  href="tel:+4540942420"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-slate-300 text-sm font-semibold rounded-none text-slate-700 bg-transparent hover:bg-slate-50 transition-colors uppercase tracking-wider"
                >
                  <Phone className="h-4 w-4 text-blue-600" />
                  Ring op
                </a>
              </div>
            </div>

            {/* Højre: Billede med infoboks */}
            <div className="relative">
              <div className="h-[350px] sm:h-[450px] lg:h-[500px] w-full rounded-none overflow-hidden shadow-2xl">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/b4f7e7e4-b57e-4a68-b3f8-2a4e7f0b8617/fpimagefixed.jpg"
                  alt="Kvalitetsbevidst tømrerarbejde og tagarbejde på Sjælland af Toa Byg ApS"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Infoboks */}
              <div className="absolute -bottom-6 -left-6 bg-white text-slate-900 p-6 rounded-none shadow-xl border border-slate-200 hidden sm:block max-w-xs">
                <p className="text-3xl font-extrabold text-black">{count}%</p>
                <p className="text-sm font-semibold uppercase tracking-wider mt-1">Kundetilfredshed</p>
                <p className="text-xs text-slate-500 mt-2">Vi forlader ikke opgaven, før du er fuldt ud tilfreds med resultatet.</p>
              </div>
            </div>
          </div>

          {/* De 4 nye værdibokse */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {/* Boks 1: Asbesttag */}
            <div className="bg-slate-50 p-8 border-t-4 border-blue-600 shadow-sm hover:shadow-md transition-all flex flex-col h-full">
              <div className="w-12 h-12 bg-blue-100 text-blue-600 flex items-center justify-center text-xl font-bold mb-6">
                <i className="fa-solid fa-shield-halved"></i>
              </div>
              <h4 className="text-lg font-bold text-slate-900 uppercase tracking-tight mb-3">
                Asbesttag
              </h4>
              <p className="text-sm text-slate-600 leading-relaxed mb-6">
                Nedtagning af <Link to="/ydelser/asbesttag" className="text-blue-600 hover:underline font-medium">asbesttag</Link> skal planlægges korrekt og udføres med fokus på sikkerhed, regler og ansvarlig håndtering, så både bygning, omgivelser og mennesker bliver beskyttet under arbejdet.
              </p>
              <div className="mt-auto pt-4">
                <Link to="/ydelser/asbesttag" className="text-blue-600 hover:text-blue-800 font-semibold text-sm inline-flex items-center gap-2 group">
                  Læs mere om asbesttag
                  <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1"></i>
                </Link>
              </div>
            </div>

            {/* Boks 2: Totalentreprise */}
            <div className="bg-slate-50 p-8 border-t-4 border-blue-600 shadow-sm hover:shadow-md transition-all flex flex-col h-full">
              <div className="w-12 h-12 bg-blue-100 text-blue-600 flex items-center justify-center text-xl font-bold mb-6">
                <i className="fa-solid fa-trowel-bricks"></i>
              </div>
              <h4 className="text-lg font-bold text-slate-900 uppercase tracking-tight mb-3">
                Totalentreprise
              </h4>
              <p className="text-sm text-slate-600 leading-relaxed">
                Vi tager os af alt fra mindre tømreropgaver til totalentreprise, hvor flere fag skal spille sammen. Det giver dig en mere overskuelig proces, fordi vi hjælper med planlægning, koordinering og udførelse.
              </p>
            </div>

            {/* Boks 3: Forebyggende tagrenovering */}
            <div className="bg-slate-50 p-8 border-t-4 border-blue-600 shadow-sm hover:shadow-md transition-all flex flex-col h-full">
              <div className="w-12 h-12 bg-blue-100 text-blue-600 flex items-center justify-center text-xl font-bold mb-6">
                <i className="fa-solid fa-house-chimney-crack"></i>
              </div>
              <h4 className="text-lg font-bold text-slate-900 uppercase tracking-tight mb-3">
                Tagrenovering
              </h4>
              <p className="text-sm text-slate-600 leading-relaxed mb-6">
                Et tag skal holde tæt, beskytte konstruktionen og give huset et sundt udgangspunkt. Vi udfører <Link to="/ydelser/tagrenovering" className="text-blue-600 hover:underline font-medium">tagrenovering</Link> i Ishøj og på resten af Sjælland, før små problemer bliver til dyre skader.
              </p>
              <div className="mt-auto pt-4">
                <Link to="/ydelser/tagrenovering" className="text-blue-600 hover:text-blue-800 font-semibold text-sm inline-flex items-center gap-2 group">
                  Læs mere om tagrenovering
                  <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1"></i>
                </Link>
              </div>
            </div>

            {/* Boks 4: Helhedsorienteret tagarbejde */}
            <div className="bg-slate-50 p-8 border-t-4 border-blue-600 shadow-sm hover:shadow-md transition-all flex flex-col h-full">
              <div className="w-12 h-12 bg-blue-100 text-blue-600 flex items-center justify-center text-xl font-bold mb-6">
                <i className="fa-solid fa-magnifying-glass-chart"></i>
              </div>
              <h4 className="text-lg font-bold text-slate-900 uppercase tracking-tight mb-3">
                Helhedsorienteret
              </h4>
              <p className="text-sm text-slate-600 leading-relaxed">
                Ved tagarbejde ser vi på helheden, ikke kun det synlige slid. Undertag, lægter, inddækninger, ventilation og tagets alder har betydning for den rigtige, mest holdbare og trygge løsning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Priseksempler Sektion */}
      <section className="py-16 lg:py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-blue-600 font-bold text-sm uppercase tracking-widest block mb-2">Gennemskuelige priser</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight uppercase">
              Priseksempler på vores arbejde
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="space-y-8">
              {priceExamples.slice(0, 3).map((item) => (
                    <div 
                      key={item.id} 
                      className="bg-white border border-slate-200/80 rounded-none shadow-sm hover:shadow-xl transition-all duration-300 relative group overflow-hidden"
                    >
                      {/* Top accent bar on hover */}
                      <div className="absolute top-0 left-0 w-full h-1 bg-blue-600 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                      
                      <div className="grid grid-cols-1 md:grid-cols-12 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                        {/* Venstre side af kortet: Info & Pris */}
                        <div className="p-6 sm:p-8 md:col-span-6 flex flex-col justify-between">
                          <div>
                            {/* Ikon & Titel */}
                            <div className="flex items-center gap-4 mb-4">
                              <div className="w-12 h-12 bg-blue-50 text-blue-600 flex items-center justify-center text-xl flex-shrink-0 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                                <i className={item.icon_name || "fa-solid fa-calculator"}></i>
                              </div>
                              <div>
                                <h3 className="text-base font-extrabold text-slate-900 uppercase tracking-tight leading-tight">
                                  {item.title}
                                </h3>
                                <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5 font-semibold">Fast prisoverslag</p>
                              </div>
                            </div>

                            {/* Beskrivelse */}
                            <p className="text-slate-600 text-sm leading-relaxed mb-6">
                              {item.description}
                            </p>
                          </div>

                          {/* Pris-bjælke */}
                          <div className="py-4 px-5 bg-slate-50 border border-slate-100 flex items-center justify-between group-hover:bg-blue-50/40 transition-colors duration-300">
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Pris</span>
                            <div className="text-right">
                              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">Fra</span>
                              <span className="text-2xl sm:text-3xl font-black text-blue-600 tracking-tight block whitespace-nowrap">
                                {item.price.replace(/^[Ff]ra\s+/, '')}
                              </span>
                              <span className="text-[9px] text-slate-400 uppercase tracking-wider block -mt-1 font-semibold">Inkl. moms & løn</span>
                            </div>
                          </div>
                        </div>

                        {/* Højre side af kortet: Inkluderet & CTA */}
                        <div className="p-6 sm:p-8 md:col-span-6 flex flex-col justify-between bg-slate-50/30">
                          <div>
                            {/* Hvad er inkluderet */}
                            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                              <i className="fa-solid fa-circle-check text-emerald-500 text-sm"></i>
                              Hvad er inkluderet?
                            </h4>
                            <div 
                              className="[&_ul]:list-none [&_ul]:space-y-2.5 [&_li]:relative [&_li]:pl-6 [&_li]:text-slate-600 [&_li]:text-xs [&_li_before]:content-['✓'] [&_li_before]:text-emerald-500 [&_li_before]:font-extrabold [&_li_before]:absolute [&_li_before]:left-0 [&_li_before]:top-0"
                              dangerouslySetInnerHTML={{ __html: item.included_items }}
                            />
                          </div>

                          <div className="mt-6 pt-4 border-t border-slate-100">
                            {/* Lille info-note */}
                            <p className="text-[10px] text-slate-400 italic leading-relaxed mb-4">
                              *Vejledende pris. Endelig pris afhænger af adgangsforhold, materialevalg og opgavens stand. Vi udarbejder altid et skriftligt tilbud efter besigtigelse.
                            </p>

                            {/* CTA */}
                            <Link
                              to={`/kontakt?emne=${encodeURIComponent(item.title)}`}
                              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border-2 border-blue-600 text-xs font-bold rounded-none text-blue-600 bg-transparent hover:bg-blue-600 hover:text-white transition-all duration-300 uppercase tracking-wider text-center group/btn"
                            >
                              Få et uforpligtende tilbud
                              <i className="fa-solid fa-arrow-right transition-transform group-hover/btn:translate-x-1"></i>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
        </div>
      </section>

      {/* Tømrer & Entreprenørarbejde Sektion */}
      <section className="py-16 lg:py-24 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Tømrer Sektion */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
            {/* Tekst */}
            <div className="space-y-6">
              <span className="text-blue-600 font-bold text-sm uppercase tracking-widest block">Tømrerarbejde</span>
              <h3 className="text-3xl font-extrabold text-slate-900 tracking-tight uppercase">
                Tømrer på Sjælland til de løsninger du bruger hver dag
              </h3>
              <p className="text-lg text-slate-600 leading-relaxed">
                Når du vælger Toa Byg ApS som tømrer på Sjælland, får du hjælp til de løsninger, der kan mærkes i hverdagen. Vi bygger terrasser, udskifter vinduer og døre, udfører indvendige ombygninger og skaber funktionelle rum, hvor finish og brugsværdi går hånd i hånd.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Renovering og ombygning handler ikke kun om nye materialer. Det handler også om at forstå, hvordan du bruger din bolig eller erhvervsejendom. Derfor lytter vi til dine ønsker, rådgiver ærligt og finder løsninger, der passer til både stil, behov og bygningens stand.
              </p>
              <div className="pt-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-sm font-semibold rounded-none text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-md uppercase tracking-wider"
                >
                  Få et uforpligtende tilbud
                </Link>
              </div>
            </div>
            {/* Billede */}
            <div className="relative">
              <div className="aspect-w-16 aspect-h-10 rounded-none overflow-hidden shadow-2xl">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/b4f7e7e4-b57e-4a68-b3f8-2a4e7f0b8617/image00008.jpeg"
                  alt="Erfaren tømrer på Sjælland udfører døre, vinduer og træterrasser for Toa Byg ApS"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Entreprenør Sektion */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Billede (først på desktop) */}
            <div className="relative order-last lg:order-first">
              <div className="aspect-w-16 aspect-h-10 rounded-none overflow-hidden shadow-2xl">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/b4f7e7e4-b57e-4a68-b3f8-2a4e7f0b8617/image00007.jpeg"
                  alt="Professionelt entreprenørarbejde og totalentreprise på Sjælland af Toa Byg ApS"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            {/* Tekst */}
            <div className="space-y-6">
              <span className="text-blue-600 font-bold text-sm uppercase tracking-widest block">Entreprenørarbejde</span>
              <h3 className="text-3xl font-extrabold text-slate-900 tracking-tight uppercase">
                Entreprenørarbejde på Sjælland med en samlet plan
              </h3>
              <p className="text-lg text-slate-600 leading-relaxed">
                Godt entreprenørarbejde på Sjælland kræver overblik, tydelig kommunikation og håndværkere, der tager ansvar for deres del af processen. Vi arbejder struktureret fra start til slut, så du ved, hvad der skal ske, hvornår det sker, og hvem der tager sig af det.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Uanset om du skal have lavet en tilbygning, en større ombygning eller et nyt byggeprojekt, kan vi hjælpe med en løsning, hvor kvaliteten er tænkt ind fra begyndelsen. Vi tror på gode dialoger, realistiske tidsplaner og håndværk, der kan holde til at blive brugt.
              </p>
              <div className="pt-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-5 py-3 border border-slate-300 text-sm font-semibold rounded-none text-slate-900 bg-transparent hover:bg-slate-50 transition-colors uppercase tracking-wider"
                >
                  Kontakt os i dag
                </Link>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Udvalgte Ydelser */}
      <section className="py-16 lg:py-24 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="mt-2 text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight uppercase">
              Vores spidskompetencer og ydelser
            </p>
            <p className="mt-4 text-lg text-slate-600">
              Vi løser en bred vifte af opgaver inden for byggeri, tømrerarbejde og tagløsninger. Her er et udvalg af vores primære ydelser.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service) => (
                <div key={service.id} className="bg-white rounded-none overflow-hidden shadow-md hover:shadow-xl transition-all flex flex-col h-full group">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image?.[0]}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-xl font-bold text-slate-900 mb-2 uppercase">{service.title}</h3>
                    <p className="text-slate-600 text-sm mb-6 flex-grow leading-relaxed">{service.description}</p>
                    <Link
                      to={`/ydelser/${service.slug?._ || service.id}`}
                      className="inline-flex items-center text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors mt-auto group/link uppercase tracking-wider"
                    >
                      Læs mere om {service.title}
                      <ArrowRight className="ml-1.5 h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}


        </div>
      </section>

      {/* Kontakt CTA Sektion */}
      <section className="pt-2 pb-10 lg:pt-4 lg:pb-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-900 rounded-none overflow-hidden shadow-2xl relative">
            {/* Dekorativ cirkel */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-600 rounded-full opacity-20" />

            {/* Skrå blå baggrund i højre side */}
            <div 
              className="absolute inset-y-0 right-0 w-1/3 bg-blue-600 hidden md:block" 
              style={{ clipPath: 'polygon(25% 0, 100% 0, 100% 100%, 0% 100%)' }}
            />

            <div className="relative z-10 p-6 sm:p-10 md:p-12 text-center">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight uppercase">
                Skal vi hjælpe dig med dit næste projekt?
              </h2>
              <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
                Uanset om du skal have renoveret taget, bygget en ny træterrasse eller står over for en større entrepriseopgave i Ishøj eller omegn, står vi klar til at hjælpe.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-6 py-3.5 border border-transparent text-base font-semibold rounded-none text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-lg uppercase tracking-wider"
                >
                  Få et uforpligtende tilbud
                </Link>
                <a
                  href="tel:+4540942420"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-white text-base font-semibold rounded-none text-white bg-slate-800 hover:bg-slate-700 transition-colors uppercase tracking-wider"
                >
                  <Phone className="h-5 w-5 text-blue-400" />
                  +45 40 94 24 20
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Galleri Preview */}
      <section className="pt-16 pb-6 lg:pt-24 lg:pb-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <div>
              <p className="mt-2 text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight uppercase">
                Se vores udførte arbejde
              </p>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {galleryItems.map((item) => {
                  // Optimer billedet til kvadratisk format (600x600px)
                  const optimizedUrl = item.imageUrl && item.imageUrl.startsWith('https://assets.auxodev.dk/')
                    ? item.imageUrl.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=600,height=600,fit=cover,quality=80,format=auto/')
                    : item.imageUrl;

                  return (
                    <Link
                      key={item.id}
                      to={`/galleri/${item.slug}`}
                      className="group relative rounded-none overflow-hidden shadow-md aspect-square bg-slate-100 block"
                    >
                      <img
                        src={optimizedUrl}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </Link>
                  );
                })}
              </div>

              <div className="mt-12 flex justify-end">
                <Link
                  to="/galleri"
                  className="inline-flex items-center text-base font-bold text-blue-600 hover:text-blue-700 transition-colors group uppercase tracking-wider border-2 border-blue-600 px-6 py-3 hover:bg-blue-50 transition-all duration-300"
                >
                  Gå til fuldt galleri
                  <ArrowRight className="ml-1.5 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Udtalelser (Testimonials) */}
      <section className="py-10 lg:py-14 bg-slate-100 text-slate-900 relative overflow-hidden border-t border-slate-200">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight uppercase text-slate-900">
              Hvad siger vores kunder?
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Navigation Arrows */}
              {testimonials.length > 1 && (
                <div className="absolute inset-y-0 -left-2 sm:-left-4 lg:-left-10 flex items-center z-20">
                  <button
                    onClick={handlePrevTestimonial}
                    className="p-1 text-slate-600 hover:text-blue-600 transition-colors cursor-pointer rounded-none"
                    aria-label="Forrige udtalelse"
                  >
                    <ChevronLeft className="h-8 w-8" />
                  </button>
                </div>
              )}
              {testimonials.length > 1 && (
                <div className="absolute inset-y-0 -right-2 sm:-right-4 lg:-right-10 flex items-center z-20">
                  <button
                    onClick={handleNextTestimonial}
                    className="p-1 text-slate-600 hover:text-blue-600 transition-colors cursor-pointer rounded-none"
                    aria-label="Næste udtalelse"
                  >
                    <ChevronRight className="h-8 w-8" />
                  </button>
                </div>
              )}

              {/* Testimonials Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {getVisibleTestimonials().map((t, index) => (
                  <div
                    key={`${t.id}-${index}`}
                    className={`bg-white border border-slate-200 p-8 rounded-none shadow-md flex flex-col justify-between transition-all duration-500 ${
                      t.visibility === 'always' ? 'flex' :
                      t.visibility === 'tablet-desktop' ? 'hidden md:flex' :
                      'hidden lg:flex'
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(t.rating || 5)].map((_, i) => (
                          <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                      <p className="text-slate-700 italic leading-relaxed text-base">
                        "{t.text}"
                      </p>
                    </div>
                    <div className="mt-6 pt-6 border-t border-slate-100 flex justify-between items-center text-sm">
                      <div>
                        <h4 className="font-bold text-slate-900 uppercase">{t.client_name}</h4>
                        <p className="text-black text-xs mt-0.5 uppercase">{t.project_type}</p>
                      </div>
                      <span className="text-slate-500 text-xs">
                        {new Date(t.date).toLocaleDateString('da-DK', { year: 'numeric', month: 'long' })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Indikator prikker */}
              {testimonials.length > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  {testimonials.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setTestimonialIndex(idx)}
                      className={`h-2 transition-all duration-300 rounded-none ${
                        idx === testimonialIndex ? 'w-8 bg-blue-600' : 'w-2 bg-slate-300 hover:bg-slate-400'
                      }`}
                      aria-label={`Gå til udtalelse ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
